import { Editor, EditorContent, useEditor } from '@tiptap/react';
import { StarterKit } from '@tiptap/starter-kit';
import { forwardRef, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { debounce } from 'throttle-debounce';

import { useOutsideClick } from '@ws/shared/hooks';

import { Highlight } from '../../TipTapEditor/plugins/Highlight';
import { TkMarker } from '../../TipTapEditor/plugins/TkMarker';

/**
 * Схема этого редактора. Вынесена и экспортирована по той же причине, что и
 * набор в text_client: узел, забытый здесь, не ломает ничего видимо — метка
 * просто перестаёт быть меткой у рецензента. Экспорт даёт тесту возможность
 * проверить состав.
 */
export const highlightsEditorExtensions = [
  StarterKit,
  Highlight.configure({ multicolor: true }),
  TkMarker,
];

type setSelection = {
  from: number;
  to: number;
  save: (commentId: string) => void;
  drop: () => void;
};

interface IHighlightsEditorProps {
  noteId: string;
  text: string;
  setSelection?: (selection: setSelection | null) => void;
}

export const HighlightsEditor = forwardRef<HTMLDivElement, IHighlightsEditorProps>(
  ({ text, setSelection }) => {
    const debouncedSetSelection = useMemo(
      () =>
        debounce(400, (selection: setSelection | null) => {
          console.log('called setSelection');
          setSelection?.(selection);
        }),
      [setSelection],
    );

    const ref = useRef<HTMLDivElement>(null);

    const [, setLocalSelection] = useState<{ from: number; to: number } | null>(null);

    const editor = useEditor({
      extensions: highlightsEditorExtensions,
      content: text,
      editable: false,
      onSelectionUpdate: ({ editor: innerEditor }) => {
        if (!setSelection) {
          return;
        }

        const save = (from: number, to: number) => (commentId: string) => {
          innerEditor
            .chain()
            .setTextSelection({ from, to })
            .setHighlight({
              color: '#ffff99',
              id: commentId,
            })
            .run();
        };

        const drop = (from: number, to: number) => () => {
          innerEditor.chain().setTextSelection({ from, to }).unsetHighlight().run();
        };

        const { from, to } = innerEditor.state.selection;
        if (from !== to) {
          setLocalSelection({ from, to });

          const saveHandler = save(from, to);
          const dropHandler = drop(from, to);
          debouncedSetSelection({ from, to, save: saveHandler, drop: dropHandler });
        } else {
          debouncedSetSelection(null);
        }
      },
    }) as Editor;

    useImperativeHandle(
      ref,
      () => {
        // eslint-disable-next-line
        return editor?.view.dom! as HTMLDivElement;
      },
      [editor],
    );

    useOutsideClick(ref, () => {
      console.log('clicked outside');
    });

    return (
      <>
        <EditorContent editor={editor} />
      </>
    );
  },
);

HighlightsEditor.displayName = 'HighlightsEditor';
